import {
  Autocomplete,
  Box,
  Button,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import Grid2 from '@mui/material/Unstable_Grid2';
import { SWTextField } from 'components/Form/Input/SWTextField';
import { Selector } from 'components/Form/Selectors/Selector';
import { InteractiveModal } from 'components/Modals/InteractiveModal/BaseModal';
import CustomerHandler from 'handlers/customer.handler';
import { useEffect, useMemo, useState } from 'react';
import { type ICreateCustomerPayload } from 'types/payloads/customer.payload.types';
import { CUSTOMER_TYPE_OPTIONS } from 'utils/enums';
import { createOrUpdateCustomerUserRequestBodySchema } from 'utils/schemas/customer.schema';
import { validateYupSchema } from 'utils/validators/schema.validator';
import { ThemePalette } from 'mui.theme';
import { useAppSelector } from 'store/hook';
import { isExternalRole } from 'utils/validators/role.validation';
import { guessUserTimezone } from 'utils/helpers/dateTime';
import { Add, Check, Delete } from '@mui/icons-material';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import dayjs from 'dayjs';
import { CustomerCodeLocationMap } from '../../utils/dummyData';
import { useAuth0 } from '@auth0/auth0-react';
import { type Customer } from 'types/customer.types';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

interface Props {
  title: string;
  open: boolean;
  handleAbort: () => void;
  enableDarkTheme: boolean;
}

interface IShift {
  startTime: string;
  endTime: string;
  isSaved?: boolean;
}

export const CreateCustomerModal = ({
  title,
  open,
  handleAbort,
  enableDarkTheme = false,
}: Props) => {
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth0();
  const accessToken = useAppSelector((state) => state.authReducer.accessToken);
  const role = useAppSelector((state) => state.authReducer.role);

  const defaultTz = useMemo(() => {
    return guessUserTimezone();
  }, []);

  const [loading, setLoading] = useState(false);
  const [isValidForm, setFormValidity] = useState(false);
  const [customer, setCustomer] = useState<ICreateCustomerPayload>({
    customerName: '',
    customerCode: '',
    customerType: 'external-customer',
    email: '',
    contactFirstName: '',
    contactLastName: '',
    phoneNumber: '',
    internalAccountingLink: '',
    unitSystem: 'metric',
    mineType: 'open_pit',
    timeZone: defaultTz,
    shifts: [],
    isTiresModule: false,
    isMachinesModule: false,
    isPartsModule: false,
    copyNavConfigCustCode: '',
    location: '',
  });

  const [selectorOptions, setSelectorOptions] = useState<
    Array<{ id: string; display: string }>
  >([]);

  const customerHandler = new CustomerHandler();

  // Validate form as user fills out fields
  useEffect(() => {
    const validateForm = async () => {
      // Check if at least one module is enabled
      const hasAtLeastOneModule =
        customer.isTiresModule ||
        customer.isMachinesModule ||
        customer.isPartsModule;

      if (!hasAtLeastOneModule) {
        setFormValidity(false);
        return;
      }

      return await validateYupSchema(
        customer,
        createOrUpdateCustomerUserRequestBodySchema
      );
    };

    void validateForm().then((isValid) => {
      setFormValidity(isValid ?? false);
    });
  }, [customer]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setCustomer({
      ...customer,
      [name]: name === 'customerCode' ? value.toLowerCase() : value,
    });
  };

  const handleSuccess = async () => {
    try {
      setLoading(true);
      const formattedShifts = customer?.shifts?.map((s) => {
        const [startH, startM] = s.startTime.split(':').map(Number);
        const [endH, endM] = s.endTime.split(':').map(Number);

        let finalEndTime = s.endTime;

        if (endH < startH || (endH === startH && endM < startM)) {
          const adjustedHour = Number(endH) + 24;
          finalEndTime = `${adjustedHour}:${endM.toString().padStart(2, '0')}`;
        }

        return {
          startTime: s.startTime,
          endTime: finalEndTime,
        };
      });

      const payload = { ...customer, shifts: formattedShifts || [] };

      await customerHandler.createCustomer(payload);
      handleAbort();
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const FILTERED_CUSTOMER_OPTIONS = useMemo(() => {
    if (role && isExternalRole(role)) {
      return [CUSTOMER_TYPE_OPTIONS[0]];
    } else {
      return CUSTOMER_TYPE_OPTIONS;
    }
  }, [role]);

  const TIMEZONE_OPTIONS = useMemo<
    Array<{ label: string; value: string }>
  >(() => {
    if (
      typeof Intl === 'undefined' ||
      typeof (Intl as any).supportedValuesOf !== 'function'
    ) {
      return [];
    }

    const timeZoneList = (
      Intl as unknown as {
        supportedValuesOf: (key: 'timeZone') => string[];
      }
    ).supportedValuesOf('timeZone');

    return timeZoneList.map((timezone: string) => ({
      value: timezone,
      label: timezone,
    }));
  }, []);

  const isShiftCycleClosed = useMemo(() => {
    if (customer.shifts.length === 0) return false;
    const lastShift = customer.shifts[customer.shifts.length - 1];

    if (!lastShift.isSaved) return false;

    return lastShift.endTime < lastShift.startTime;
  }, [customer.shifts]);

  const handleShiftChange = (
    index: number,
    field: keyof IShift,
    newValue: dayjs.Dayjs | null
  ) => {
    if (!newValue) return;
    const timeString = newValue.format('HH:mm');

    const updatedShifts = [...customer.shifts];
    updatedShifts[index] = { ...updatedShifts[index], [field]: timeString };
    setCustomer({ ...customer, shifts: updatedShifts });
  };

  const handleAddShift = () => {
    if (customer.shifts.some((s) => !s.isSaved)) return;

    let nextStartTime = '06:00';
    if (customer.shifts.length > 0) {
      nextStartTime = customer.shifts[customer.shifts.length - 1].endTime;
    }

    const [h, m] = nextStartTime.split(':').map(Number);
    const nextEndH = (h + 8) % 24;
    const nextEndTime = `${nextEndH.toString().padStart(2, '0')}:${m
      .toString()
      .padStart(2, '0')}`;

    setCustomer({
      ...customer,
      shifts: [
        ...customer.shifts,
        {
          startTime: nextStartTime,
          endTime: nextEndTime,
          isSaved: false,
        },
      ],
    });
  };

  const handleSaveShift = (index: number) => {
    const updatedShifts = [...customer.shifts];
    updatedShifts[index] = { ...updatedShifts[index], isSaved: true };
    setCustomer({ ...customer, shifts: updatedShifts });
  };

  const handleRemoveShift = (index: number) => {
    const updatedShifts = customer.shifts.filter((_, i) => i !== index);
    setCustomer({ ...customer, shifts: updatedShifts });
  };

  const getDayjsFromTime = (timeStr: string) => {
    return dayjs(timeStr, 'HH:mm');
  };

  const fetchCustomers = async () => {
    try {
      const customers: Customer[] = await customerHandler.getAll(false);
      if (Array.isArray(customers) && customers.length) {
        const options = customers.map((customer) => ({
          id: customer.customerId,
          display: customer.customerName,
        }));
        setSelectorOptions(options);
      } else {
        throw new Error('INvalid customers data ');
      }
    } catch (error) {
      console.error('Failed to fetch customer selector options', error);
      setSelectorOptions([]);
    }
  };

  useEffect(() => {
    if (!isAuthenticated || !accessToken || !role) return;
    void fetchCustomers();
  }, [role, isAuthenticated, accessToken]);

  return (
    <InteractiveModal
      initialState={open}
      SuccessButtonProps={{
        disabled: !isValidForm || loading,
        onClick: handleSuccess,
        label: t('create_customer_account'),
      }}
      handleAbort={handleAbort}
      title={title}
      confirmCancellation
      enableDarkTheme={enableDarkTheme}
    >
      {/* First Name TextField */}
      <SWTextField
        key="customer-name"
        name="customerName"
        label={t('company_name')}
        TextFieldProps={{
          name: 'customerName',
          value: customer.customerName,
          onChange: handleChange,
          size: 'small',
          InputProps: {
            size: 'small',
            fullWidth: true,
            sx: {
              width: '260px',
              '& fieldset': {
                borderColor: '#38465F',
              },
              color: palette.textPrimary,
            },
          },
        }}
        enableDarkTheme={enableDarkTheme}
      />
      {/* Customer Code TextField */}

      {role === 'external-director' ? (
        // (ONE Selector for Code + Location) ---
        <Box key="customer-code-location-combined">
          <Typography
            variant="bodyBold"
            display="block"
            mb={'4px'}
            sx={{
              color: palette.textPrimary,
            }}
          >
            {t('company_code_location')}
          </Typography>
          <Selector
            value={
              customer.customerCode && customer.location
                ? `${customer.customerCode},${customer.location}`
                : ''
            }
            onChange={(event) => {
              const selectedValue = event.target.value as string;

              // parts[0] = Code, parts[1..n] = Location details
              const parts = selectedValue.split(',');

              if (parts.length > 0) {
                const newCode = parts[0].trim();
                const newLocation = parts.slice(1).join(',').trim();
                setCustomer({
                  ...customer,
                  customerCode: newCode,
                  location: newLocation,
                });
              }
            }}
            selectorOptions={CustomerCodeLocationMap}
            enableDarkTheme={enableDarkTheme}
            height="26px"
            minWidth="260px"
            fontSize="14px"
          />
        </Box>
      ) : (
        <>
          <Box key="customer-code">
            {/* Field 1: Customer Code */}
            <SWTextField
              key="customer-code"
              placeholder={t('only_3_characters')}
              name="customerCode"
              label={t('company_code')}
              TextFieldProps={{
                name: 'customerCode',
                value: customer.customerCode,
                onChange: handleChange,
                size: 'small',
                InputProps: {
                  size: 'small',
                  fullWidth: true,
                  sx: {
                    width: '260px',
                    '& fieldset': { borderColor: '#38465F' },
                    color: palette.textPrimary,
                  },
                },
              }}
              enableDarkTheme={enableDarkTheme}
            />
          </Box>
          <Box key="location">
            {/* Field 2: Customer Location */}
            <SWTextField
              key="customer-location"
              name="customerLocation"
              label={t('location_city_state_country')}
              TextFieldProps={{
                name: 'location',
                value: customer.location,
                onChange: handleChange,
                size: 'small',
                InputProps: {
                  size: 'small',
                  fullWidth: true,
                  sx: {
                    width: '260px',
                    '& fieldset': { borderColor: '#38465F' },
                    color: palette.textPrimary,
                  },
                },
              }}
              enableDarkTheme={enableDarkTheme}
            />
          </Box>
        </>
      )}

      {/* <SWTextField
        key="customer-code"
        placeholder="Only 3 characters"
        name="customerCode"
        label="Company Code*"
        TextFieldProps={{
          name: 'customerCode',
          value: customer.customerCode,
          onChange: handleChange,
          size: 'small',
          InputProps: {
            size: 'small',
            fullWidth: true,
            sx: {
              width: '260px',
              '& fieldset': {
                borderColor: '#38465F',
              },
              color: palette.textPrimary,
            },
          },
        }}
        enableDarkTheme={enableDarkTheme}
      /> */}

      {/* Department selector drop down single */}
      <Box key="customer-type">
        <Typography
          variant="bodyBold"
          display="block"
          mb={'4px'}
          sx={{
            color: palette.textPrimary,
          }}
        >
          {t('customer_type')}
        </Typography>
        <Selector
          value={customer.customerType}
          onChange={(event, child) => {
            setCustomer({
              ...customer,
              customerType:
                event.target.value.toString() as ICreateCustomerPayload['customerType'],
            });
          }}
          selectorOptions={FILTERED_CUSTOMER_OPTIONS}
          enableDarkTheme={enableDarkTheme}
          height="26px"
          minWidth="260px"
          fontSize="14px"
        />
      </Box>
      <Box key="unit-system">
        <Typography
          variant="bodyBold"
          display="block"
          mb={'4px'}
          sx={{
            color: palette.textPrimary,
          }}
        >
          {t('unit_system')}
        </Typography>
        <RadioGroup
          row
          aria-labelledby="unitSystem"
          name="unitSystem"
          onChange={handleChange}
          defaultValue="metric"
          value={customer.unitSystem}
        >
          <FormControlLabel
            value="metric"
            control={
              <Radio
                sx={{
                  color: enableDarkTheme
                    ? palette.primary
                    : ThemePalette.typography.primary,
                  '&.Mui-checked': {
                    color: enableDarkTheme
                      ? palette.primary
                      : ThemePalette.typography.primary,
                  },
                }}
              />
            }
            label={
              <Typography
                variant="body2"
                style={{ fontSize: '12px' }}
                sx={{
                  color: palette.textPrimary,
                }}
              >
                {t('metric_kms_kgs_litres')}
              </Typography>
            }
          />
          <FormControlLabel
            value="imperial"
            control={
              <Radio
                sx={{
                  color: enableDarkTheme
                    ? palette.primary
                    : ThemePalette.typography.primary,
                  '&.Mui-checked': {
                    color: enableDarkTheme
                      ? palette.primary
                      : ThemePalette.typography.primary,
                  },
                }}
              />
            }
            label={
              <Typography
                variant="body2"
                style={{ fontSize: '12px' }}
                sx={{
                  color: palette.textPrimary,
                }}
              >
                {t('imperial_miles_pounds_gallons')}
              </Typography>
            }
          />
        </RadioGroup>
      </Box>

      <Box key="time-zone">
        <Typography
          variant="bodyBold"
          display="block"
          mb={'4px'}
          sx={{
            color: palette.textPrimary,
          }}
        >
          {t('time_zone')}
        </Typography>

        <Autocomplete
          options={TIMEZONE_OPTIONS}
          getOptionLabel={(option) => option.label}
          size="small"
          value={
            TIMEZONE_OPTIONS.find(
              (option) => option.value === customer.timeZone
            ) ?? undefined
          }
          onChange={(_, selectedOption) => {
            setCustomer({
              ...customer,
              timeZone: selectedOption?.value ?? 'UTC',
            });
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={t('search_time_zone')}
              size="small"
              InputProps={{
                ...params.InputProps,
                sx: {
                  width: '260px',
                  fontSize: '14px',

                  color: palette.textPrimary,
                },
              }}
            />
          )}
          disableClearable
          sx={{
            color: palette.textPrimary,
            '& fieldset': {
              borderColor: '#38465F',
            },
          }}
        />
      </Box>

      <Box key="mine-type">
        <Typography
          variant="bodyBold"
          display="block"
          mb={'4px'}
          sx={{
            color: palette.textPrimary,
          }}
        >
          {t('mine_type')}
        </Typography>
        <RadioGroup
          row
          aria-labelledby="mineType"
          name="mineType"
          onChange={handleChange}
          defaultValue="open_pit"
          value={customer.mineType}
        >
          <FormControlLabel
            value="open_pit"
            control={
              <Radio
                sx={{
                  color: enableDarkTheme
                    ? palette.primary
                    : ThemePalette.typography.primary,
                  '&.Mui-checked': {
                    color: enableDarkTheme
                      ? palette.primary
                      : ThemePalette.typography.primary,
                  },
                }}
              />
            }
            label={
              <Typography
                variant="body2"
                style={{ fontSize: '12px' }}
                sx={{
                  color: palette.textPrimary,
                }}
              >
                {t('open_pit')}
              </Typography>
            }
          />
          <FormControlLabel
            value="underground"
            control={
              <Radio
                sx={{
                  color: enableDarkTheme
                    ? palette.primary
                    : ThemePalette.typography.primary,
                  '&.Mui-checked': {
                    color: enableDarkTheme
                      ? palette.primary
                      : ThemePalette.typography.primary,
                  },
                }}
              />
            }
            label={
              <Typography
                variant="body2"
                style={{ fontSize: '12px' }}
                sx={{
                  color: palette.textPrimary,
                }}
              >
                Underground
              </Typography>
            }
          />
        </RadioGroup>
      </Box>

      <Box key="module-switches">
        <Typography
          variant="bodyBold"
          display="block"
          mb={'4px'}
          sx={{
            color: palette.textPrimary,
          }}
        >
          {t('module_configuration')}
        </Typography>
        <Typography
          variant="caption"
          display="block"
          mb={'12px'}
          sx={{
            color: enableDarkTheme
              ? 'rgba(255, 255, 255, 0.6)'
              : 'rgba(0, 0, 0, 0.5)',
            fontSize: '11px',
          }}
        >
          {t('at_least_one_module_must_be_enabled')}
        </Typography>

        <FormControlLabel
          control={
            <Switch
              checked={customer.isTiresModule}
              onChange={(e) => {
                setCustomer({
                  ...customer,
                  isTiresModule: e.target.checked,
                });
              }}
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': {
                  color: enableDarkTheme
                    ? palette.primary
                    : ThemePalette.typography.primary,
                },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                  backgroundColor: enableDarkTheme
                    ? palette.primary
                    : ThemePalette.typography.primary,
                },
              }}
            />
          }
          label={
            <Typography
              variant="body2"
              sx={{
                color: palette.textPrimary,
              }}
            >
              X.Tires
            </Typography>
          }
          labelPlacement="start"
          sx={{
            marginLeft: 0,
            justifyContent: 'space-between',
            width: '100%',
            maxWidth: '260px',
          }}
        />

        <FormControlLabel
          control={
            <Switch
              checked={customer.isMachinesModule}
              onChange={(e) => {
                setCustomer({
                  ...customer,
                  isMachinesModule: e.target.checked,
                });
              }}
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': {
                  color: enableDarkTheme
                    ? palette.primary
                    : ThemePalette.typography.primary,
                },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                  backgroundColor: enableDarkTheme
                    ? palette.primary
                    : ThemePalette.typography.primary,
                },
              }}
            />
          }
          label={
            <Typography
              variant="body2"
              sx={{
                color: palette.textPrimary,
              }}
            >
              X.Machines
            </Typography>
          }
          labelPlacement="start"
          sx={{
            marginLeft: 0,
            justifyContent: 'space-between',
            width: '100%',
            maxWidth: '260px',
          }}
        />

        <FormControlLabel
          control={
            <Switch
              checked={customer.isPartsModule}
              onChange={(e) => {
                setCustomer({
                  ...customer,
                  isPartsModule: e.target.checked,
                });
              }}
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': {
                  color: enableDarkTheme
                    ? palette.primary
                    : ThemePalette.typography.primary,
                },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                  backgroundColor: enableDarkTheme
                    ? palette.primary
                    : ThemePalette.typography.primary,
                },
              }}
            />
          }
          label={
            <Typography
              variant="body2"
              sx={{
                color: palette.textPrimary,
              }}
            >
              X.Parts
            </Typography>
          }
          labelPlacement="start"
          sx={{
            marginLeft: 0,
            justifyContent: 'space-between',
            width: '100%',
            maxWidth: '260px',
          }}
        />
      </Box>

      <Box key="copy-nav-config">
        <Typography
          variant="bodyBold"
          display="block"
          mb={'4px'}
          sx={{
            color: palette.textPrimary,
          }}
        >
          {t('copy_nav_config')}
        </Typography>
        <Selector
          value={customer.copyNavConfigCustCode}
          onChange={(event) => {
            setCustomer({
              ...customer,
              copyNavConfigCustCode: event.target.value as string,
            });
          }}
          selectorOptions={selectorOptions}
          enableDarkTheme={enableDarkTheme}
          height="26px"
          minWidth="260px"
          fontSize="14px"
        />
      </Box>

      <Grid2 xs={12} lg={12} padding={'15px'}>
        <Box key="shift-config">
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={'8px'}
          >
            <Typography
              variant="bodyBold"
              sx={{
                color: palette.textPrimary,
              }}
            >
              {t('shift_configuration')}
            </Typography>
            <Button
              startIcon={<Add />}
              onClick={handleAddShift}
              size="small"
              disabled={
                customer.shifts.some((s) => !s.isSaved) || isShiftCycleClosed
              }
              sx={{
                textTransform: 'none',
                color: enableDarkTheme
                  ? palette.primary
                  : ThemePalette.typography.primary,
              }}
            >
              {t('add_shift')}
            </Button>
          </Box>

          {customer.shifts.map((shift, index) => {
            const prevShiftEndTime =
              index > 0
                ? getDayjsFromTime(customer.shifts[index - 1].endTime)
                : undefined;

            return (
              <Box key={`shift-${index}`} padding={0}>
                <Grid2 container spacing={2}>
                  {/* Start Time */}
                  <Grid2 xs={5}>
                    <TimePicker
                      disabled={shift.isSaved}
                      value={getDayjsFromTime(shift.startTime)}
                      onChange={(newValue) => {
                        handleShiftChange(index, 'startTime', newValue);
                      }}
                      label={'Start time'}
                      minTime={prevShiftEndTime}
                      slotProps={{
                        textField: {
                          size: 'small',
                          fullWidth: true,
                          sx: {
                            minWidth: '0px',
                            '& fieldset': {
                              borderColor: '#38465F',
                            },
                          },
                          inputProps: {
                            style: {
                              color: palette.textPrimary,
                            },
                          },
                        },
                      }}
                      ampm={false}
                    />
                  </Grid2>

                  <Grid2 xs={5}>
                    <TimePicker
                      label={t('end_time_2')}
                      disabled={shift.isSaved}
                      value={getDayjsFromTime(shift.endTime)}
                      onChange={(newValue) => {
                        handleShiftChange(index, 'endTime', newValue);
                      }}
                      slotProps={{
                        textField: {
                          size: 'small',
                          fullWidth: true,
                          sx: {
                            minWidth: '0px',
                            '& fieldset': {
                              borderColor: '#38465F',
                            },
                          },
                          inputProps: {
                            style: {
                              color: palette.textPrimary,
                            },
                          },
                        },
                      }}
                      ampm={false}
                    />
                  </Grid2>

                  <Grid2 xs={2} display="flex" gap={1} alignItems={'center'}>
                    {!shift.isSaved && (
                      <IconButton
                        onClick={() => {
                          handleSaveShift(index);
                        }}
                        size="small"
                        title={t('save_shift')}
                      >
                        <Check
                          fontSize="small"
                          sx={{
                            color: enableDarkTheme ? '#4caf50' : 'success.main',
                          }}
                        />
                      </IconButton>
                    )}
                    <IconButton
                      onClick={() => {
                        handleRemoveShift(index);
                      }}
                      size="small"
                      title={t('delete_shift')}
                      disabled={index !== customer.shifts.length - 1}
                    >
                      <Delete
                        fontSize="small"
                        sx={{
                          color:
                            index !== customer.shifts.length - 1
                              ? 'grey'
                              : enableDarkTheme
                              ? '#ff6b6b'
                              : 'error.main',
                        }}
                      />
                    </IconButton>
                  </Grid2>
                </Grid2>
              </Box>
            );
          })}

          {isShiftCycleClosed && (
            <Typography
              variant="caption"
              sx={{
                fontStyle: 'italic',
                color: enableDarkTheme ? palette.primary : 'primary.main',
                mt: 1,
                display: 'block',
              }}
            >
              {t(
                'shift_cycle_closed_crosses_midnight_no_further_shifts_can_be'
              )}
            </Typography>
          )}
        </Box>
      </Grid2>

      <Grid2 xs={12} lg={12} padding={'15px'}>
        <Box
          sx={{
            height: '35px',
            backgroundColor: '#7485A2',
            display: 'flex',
            alignItems: 'center',
            paddingLeft: '14px',
            paddingTop: '8px',
            paddingBottom: '8px',
          }}
        >
          <Typography
            color="white"
            variant="bodyBold"
            sx={{
              color: palette.textPrimary,
            }}
          >
            {t('company_contact_information')}
          </Typography>
        </Box>
      </Grid2>

      {/* First Name TextField */}
      <SWTextField
        key="contact-first-name"
        name="contactFirstName"
        label={t('first_name')}
        TextFieldProps={{
          name: 'contactFirstName',
          value: customer.contactFirstName,
          onChange: handleChange,
          InputProps: {
            size: 'small',
            fullWidth: true,
            sx: {
              width: '260px',
              '& fieldset': {
                borderColor: '#38465F',
              },
              color: palette.textPrimary,
            },
          },
        }}
        enableDarkTheme={enableDarkTheme}
      />
      {/* Last Name TextField */}
      <SWTextField
        key="contact-last-name"
        name="contactLastName"
        label={t('last_name')}
        TextFieldProps={{
          name: 'contactLastName',
          value: customer.contactLastName,
          onChange: handleChange,
          InputProps: {
            size: 'small',
            fullWidth: true,
            sx: {
              width: '260px',
              '& fieldset': {
                borderColor: '#38465F',
              },
              color: palette.textPrimary,
            },
          },
        }}
        enableDarkTheme={enableDarkTheme}
      />

      {/* Email  TextField */}
      <Box key="email">
        <Typography
          variant="bodyBold"
          display="block"
          mb={'4px'}
          sx={{
            color: palette.textPrimary,
          }}
        >
          {t('email_address')}
        </Typography>

        <TextField
          name="email"
          onChange={handleChange}
          value={customer.email}
          size="small"
          InputProps={{
            fullWidth: true,
            sx: {
              width: '260px',
              '& fieldset': {
                borderColor: '#38465F',
              },
              color: palette.textPrimary,
            },
          }}
        />
      </Box>

      {/* Phone Number TextField */}
      <Box key="phone-number">
        <Typography
          variant="bodyBold"
          display="block"
          mb={'4px'}
          sx={{
            color: palette.textPrimary,
          }}
        >
          {t('phone_number')}
        </Typography>

        <TextField
          name="phoneNumber"
          onChange={handleChange}
          value={customer.phoneNumber}
          size="small"
          InputProps={{
            fullWidth: true,
            sx: {
              width: '260px',
              '& fieldset': {
                borderColor: '#38465F',
              },
              color: palette.textPrimary,
            },
          }}
        />
      </Box>

      {/* Phone Number TextField */}
      <Box key="internal-accounting-number">
        <Typography
          variant="bodyBold"
          display="block"
          mb={'4px'}
          sx={{
            color: palette.textPrimary,
          }}
        >
          {t('internal_accounting_link')}
        </Typography>

        <TextField
          name="internalAccountingLink"
          onChange={handleChange}
          value={customer.internalAccountingLink}
          size="small"
          InputProps={{
            fullWidth: true,
            sx: {
              width: '260px',
              '& fieldset': {
                borderColor: '#38465F',
              },
              color: palette.textPrimary,
            },
          }}
        />
      </Box>
    </InteractiveModal>
  );
};
